import React, { useEffect, useRef, useState } from 'react';
import './PortfolioGame.css';

const tokens = [
    { id: 'home', label: 'Home' },
    { id: 'about-me', label: 'Biography' },
    { id: 'experience', label: 'Experience' },
    { id: 'projects', label: 'Projects' },
];

const keyDirections = {
    ArrowDown: 'down',
    ArrowLeft: 'left',
    ArrowRight: 'right',
    ArrowUp: 'up',
    KeyA: 'left',
    KeyD: 'right',
    KeyS: 'down',
    KeyW: 'up',
};

const getTokenPositions = (layouts) => tokens.flatMap((token, index) => {
    const anchor = document.getElementById(token.id);
    if (!anchor) return [];

    const bounds = anchor.getBoundingClientRect();
    const sectionStart = window.scrollY + bounds.top;
    const nextToken = tokens[index + 1];
    const nextAnchor = nextToken
        ? document.getElementById(nextToken.id)
        : document.querySelector('footer');
    const sectionEnd = nextAnchor
        ? window.scrollY + nextAnchor.getBoundingClientRect().top
        : document.documentElement.scrollHeight;
    const topInset = token.id === 'home'
        ? Math.min(window.innerHeight * 0.42, 300)
        : 100;
    const bottomInset = 90;
    const placementStart = sectionStart + topInset;
    const placementEnd = Math.max(placementStart, sectionEnd - bottomInset);
    const layout = layouts[index] || { horizontal: 0.5, vertical: 0.5 };

    return [{
        ...token,
        x: window.innerWidth * (0.18 + layout.horizontal * 0.64),
        y: placementStart + (placementEnd - placementStart) * layout.vertical,
    }];
});

const PortfolioGame = () => {
    const [active, setActive] = useState(false);
    const [player, setPlayer] = useState({ x: 64, y: 160 });
    const [collected, setCollected] = useState([]);
    const [tokenPositions, setTokenPositions] = useState([]);
    const [scrollTop, setScrollTop] = useState(0);
    const scrollTopRef = useRef(0);
    const playerRef = useRef(player);
    const keysPressed = useRef(new Set());
    const tokenPositionsRef = useRef([]);
    const tokenLayoutsRef = useRef([]);
    const collectedRef = useRef(new Set());

    const startGame = () => {
        const startingPosition = {
            x: Math.min(64, window.innerWidth * 0.2),
            y: Math.min(160, window.innerHeight * 0.28),
            angle: 0,
        };

        keysPressed.current.clear();
        collectedRef.current.clear();
        tokenLayoutsRef.current = tokens.map(() => ({
            horizontal: Math.random(),
            vertical: Math.random(),
        }));
        tokenPositionsRef.current = [];
        setTokenPositions([]);
        playerRef.current = startingPosition;
        setCollected([]);
        setPlayer(startingPosition);
        document.documentElement.classList.add('portfolio-game-active');
        window.scrollTo(0, 0);
        scrollTopRef.current = 0;
        setScrollTop(0);
        setActive(true);
    };

    const exitGame = () => {
        keysPressed.current.clear();
        document.documentElement.classList.remove('portfolio-game-active');
        setActive(false);
    };

    useEffect(() => {
        if (!active) return undefined;

        let animationFrame;
        let previousFrameTime = 0;
        const speed = 300;
        const pressedDirections = keysPressed.current;

        const measureTokens = () => {
            const measuredTokens = getTokenPositions(tokenLayoutsRef.current);
            tokenPositionsRef.current = measuredTokens;
            setTokenPositions(measuredTokens);
        };

        const handleKeyDown = (event) => {
            const direction = keyDirections[event.code];
            if (!direction || event.altKey || event.ctrlKey || event.metaKey) return;

            const target = event.target;
            if (target instanceof HTMLElement && target.closest('input, textarea, select, [contenteditable="true"]')) {
                return;
            }

            event.preventDefault();
            pressedDirections.add(direction);
        };

        const handleKeyUp = (event) => {
            const direction = keyDirections[event.code];
            if (direction) pressedDirections.delete(direction);
        };

        const clearPressedKeys = () => pressedDirections.clear();

        measureTokens();
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('resize', measureTokens);
        window.addEventListener('blur', clearPressedKeys);

        const animate = (frameTime) => {
            const elapsed = previousFrameTime
                ? Math.min((frameTime - previousFrameTime) / 1000, 0.05)
                : 0;
            previousFrameTime = frameTime;

            const directions = pressedDirections;
            const horizontalDirection = Number(directions.has('right')) - Number(directions.has('left'));
            const verticalDirection = Number(directions.has('down')) - Number(directions.has('up'));
            const movementScale = horizontalDirection && verticalDirection ? Math.SQRT1_2 : 1;
            const moving = horizontalDirection !== 0 || verticalDirection !== 0;
            const maximumX = Math.max(24, window.innerWidth - 24);
            const maximumY = Math.max(24, document.documentElement.scrollHeight - 24);
            const currentPlayer = playerRef.current;
            const nextPlayer = {
                x: Math.max(24, Math.min(maximumX, currentPlayer.x + horizontalDirection * speed * elapsed * movementScale)),
                y: Math.max(24, Math.min(maximumY, currentPlayer.y + verticalDirection * speed * elapsed * movementScale)),
                angle: horizontalDirection !== 0
                    ? (horizontalDirection > 0 ? 0 : 180)
                    : verticalDirection !== 0
                        ? (verticalDirection > 0 ? 90 : -90)
                        : currentPlayer.angle,
                moving,
            };

            const screenY = nextPlayer.y - window.scrollY;
            if (screenY > window.innerHeight * 0.72) {
                window.scrollTo(0, Math.min(maximumY, nextPlayer.y - window.innerHeight * 0.62));
            } else if (screenY < window.innerHeight * 0.22 && nextPlayer.y > window.scrollY) {
                window.scrollTo(0, Math.max(0, nextPlayer.y - window.innerHeight * 0.32));
            }

            const nextScrollTop = window.scrollY;
            if (nextScrollTop !== scrollTopRef.current) {
                scrollTopRef.current = nextScrollTop;
                setScrollTop(nextScrollTop);
            }

            if (moving || currentPlayer.moving) {
                playerRef.current = nextPlayer;
                setPlayer(nextPlayer);
            }

            const newlyCollected = tokenPositionsRef.current.filter((token) => (
                !collectedRef.current.has(token.id)
                && Math.hypot(nextPlayer.x - token.x, nextPlayer.y - token.y) < 42
            ));

            if (newlyCollected.length > 0) {
                newlyCollected.forEach((token) => collectedRef.current.add(token.id));
                setCollected(Array.from(collectedRef.current));
            }

            animationFrame = window.requestAnimationFrame(animate);
        };

        animationFrame = window.requestAnimationFrame(animate);

        return () => {
            window.cancelAnimationFrame(animationFrame);
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('resize', measureTokens);
            window.removeEventListener('blur', clearPressedKeys);
            document.documentElement.classList.remove('portfolio-game-active');
            pressedDirections.clear();
        };
    }, [active]);

    const pressDirection = (direction) => (event) => {
        event.preventDefault();
        keysPressed.current.add(direction);
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const releaseDirection = (direction) => () => keysPressed.current.delete(direction);

    return (
        <>
            {!active ? (
                <div className="portfolio-game-launch-wrap">
                    <button
                        type="button"
                        className="portfolio-game-launch"
                        aria-describedby="portfolio-game-controls-help"
                        onClick={startGame}
                    >
                        Play token hunt
                    </button>
                    <span id="portfolio-game-controls-help" className="portfolio-game-help" role="tooltip">
                        <strong>WASD or arrow keys</strong>
                        <span>Move Pac-Man and collect one token in each section.</span>
                        <span>Use the close button to leave the hunt.</span>
                    </span>
                </div>
            ) : (
                <div className="portfolio-game" role="region" aria-label="Portfolio token hunt">
                    <div className="portfolio-game-hud">
                        <span className="portfolio-game-score" aria-hidden="true">
                            <span className="portfolio-game-score-value">{collected.length}</span>
                            <span className="portfolio-game-score-divider">/</span>
                            {tokens.length}
                        </span>
                        <span className="visually-hidden" aria-live="polite">
                            {collected.length} of {tokens.length} tokens collected
                        </span>
                        <button
                            type="button"
                            className="portfolio-game-exit"
                            aria-label="Exit token hunt"
                            onClick={exitGame}
                        >
                            &times;
                        </button>
                    </div>

                    <div className="portfolio-game-collectibles" aria-hidden="true">
                        {tokenPositions.map((token) => (
                            <span
                                key={token.id}
                                className={`portfolio-game-token${collected.includes(token.id) ? ' is-collected' : ''}`}
                                style={{ left: token.x, top: token.y - scrollTop }}
                            >
                                <span className="portfolio-game-token-core" />
                            </span>
                        ))}
                    </div>

                    <div
                        className={`portfolio-game-player${player.moving ? ' is-moving' : ''}`}
                        aria-hidden="true"
                        style={{
                            left: player.x,
                            top: player.y - scrollTop,
                            '--player-angle': `${player.angle}deg`,
                        }}
                    />

                    {collected.length === tokens.length && (
                        <div className="portfolio-game-complete" role="status">
                            All tokens collected
                        </div>
                    )}

                    <div className="portfolio-game-pad" aria-label="Movement controls">
                        <button
                            type="button"
                            className="game-pad-button game-pad-up"
                            aria-label="Move up"
                            onPointerDown={pressDirection('up')}
                            onPointerUp={releaseDirection('up')}
                            onPointerCancel={releaseDirection('up')}
                            onLostPointerCapture={releaseDirection('up')}
                        >
                            &#8593;
                        </button>
                        <button
                            type="button"
                            className="game-pad-button game-pad-left"
                            aria-label="Move left"
                            onPointerDown={pressDirection('left')}
                            onPointerUp={releaseDirection('left')}
                            onPointerCancel={releaseDirection('left')}
                            onLostPointerCapture={releaseDirection('left')}
                        >
                            &#8592;
                        </button>
                        <button
                            type="button"
                            className="game-pad-button game-pad-down"
                            aria-label="Move down"
                            onPointerDown={pressDirection('down')}
                            onPointerUp={releaseDirection('down')}
                            onPointerCancel={releaseDirection('down')}
                            onLostPointerCapture={releaseDirection('down')}
                        >
                            &#8595;
                        </button>
                        <button
                            type="button"
                            className="game-pad-button game-pad-right"
                            aria-label="Move right"
                            onPointerDown={pressDirection('right')}
                            onPointerUp={releaseDirection('right')}
                            onPointerCancel={releaseDirection('right')}
                            onLostPointerCapture={releaseDirection('right')}
                        >
                            &#8594;
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default PortfolioGame;